import { Box, Divider, Typography } from '@mui/material';
import { ReactElement } from 'react';

const Section = ({ title, children }: { title: string; children: React.ReactNode }): ReactElement => (
  <Box mt={6}>
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

/**
 * Public privacy policy. Served without authentication — carriers require a
 * publicly accessible privacy policy URL for toll-free SMS verification, and
 * the policy must state that mobile opt-in data is not shared with third
 * parties for marketing.
 */
export const Privacy = (): ReactElement => (
  <Box sx={{ maxWidth: 720, mx: 'auto', px: 4, py: 8 }}>
    <Typography variant="h3" component="h1" gutterBottom>
      Privacy Policy
    </Typography>
    <Typography variant="body2" color="text.secondary" mb={4}>
      The Tag System · Last updated: August 15, 2026
    </Typography>

    <Typography paragraph>
      The Tag System (&ldquo;we,&rdquo; &ldquo;us&rdquo;) provides a business asset-tracking and management platform used by companies to track equipment,
      vehicles, and job-site activity. Our service is provided to businesses, not to individual consumers. This policy describes the limited personal
      information we handle and how we use it.
    </Typography>
    <Divider />

    <Section title="Information we collect">
      <ul>
        <li>
          <Typography paragraph>
            <strong>Account and contact information.</strong> When a customer organization sets up user accounts for its staff, we receive names, work email
            addresses, and mobile phone numbers, provided by the organization&rsquo;s administrator or by the user during account setup.
          </Typography>
        </li>
        <li>
          <Typography paragraph>
            <strong>Operational data.</strong> Equipment scans, vehicle and job-site locations, and related activity generated by our customers&rsquo; use of
            the platform. This data belongs to the customer organization and is processed on its behalf.
          </Typography>
        </li>
        <li>
          <Typography paragraph>
            <strong>Usage and log data.</strong> Standard technical records such as login times and system logs used to operate and secure the service.
          </Typography>
        </li>
      </ul>
    </Section>

    <Section title="How we use information">
      <ul>
        <li>
          <Typography paragraph>To provide and operate the platform for the customer organization.</Typography>
        </li>
        <li>
          <Typography paragraph>
            To send service communications: account invitations, password resets, login verification codes, operational alerts, and scheduled reports, by email
            and text message (SMS).
          </Typography>
        </li>
        <li>
          <Typography paragraph>To provide support and maintain the security and reliability of the service.</Typography>
        </li>
      </ul>
      <Typography paragraph>We do not send marketing or promotional messages to platform users, and we do not sell personal information.</Typography>
    </Section>

    <Section title="Text messages (SMS)">
      <Typography paragraph>
        The platform sends operational text messages — such as equipment alerts and login verification codes — to mobile numbers registered to a user&rsquo;s
        account. Users consent to these messages when their organization registers their number and they verify it during account setup. Message frequency
        varies with operational activity. Message and data rates may apply.
      </Typography>
      <Typography paragraph>
        You may opt out of text messages at any time by replying <strong>STOP</strong>, or by asking your organization&rsquo;s administrator to remove or
        deactivate your mobile number. Reply <strong>HELP</strong> or contact us at the address below for assistance.
      </Typography>
      <Typography paragraph sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 3, py: 1 }}>
        No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and
        consent are not shared with any third parties.
      </Typography>
    </Section>

    <Section title="How we share information">
      <Typography paragraph>
        We share information only with service providers that help us operate the platform (such as cloud hosting and message-delivery providers), and only as
        needed to deliver the service; with the customer organization that administers your account; and where required by law. We do not sell or rent personal
        information to anyone.
      </Typography>
    </Section>

    <Section title="Data retention and security">
      <Typography paragraph>
        We retain account information for as long as the customer organization maintains the account, and operational data per our agreement with that
        organization. We use industry-standard safeguards, including encrypted connections and access controls, to protect the information we handle.
      </Typography>
    </Section>

    <Section title="Your choices">
      <Typography paragraph>
        Because accounts are administered by your employer or contracting organization, requests to access, correct, or delete your account information can be
        made through your organization&rsquo;s administrator or directly to us at the address below.
      </Typography>
    </Section>

    <Section title="Contact">
      <Typography paragraph>
        Questions about this policy or our data practices: <a href="mailto:support@thetagsystem.com">support@thetagsystem.com</a>
      </Typography>
    </Section>

    <Section title="Changes to this policy">
      <Typography paragraph>
        We may update this policy from time to time; the date above reflects the latest revision. Material changes will be communicated to customer
        organizations.
      </Typography>
    </Section>
  </Box>
);

export default Privacy;
